import { route, SandboxError, type Route } from '../adapter';
import { matches, newId, now, paginate, type SandboxStore } from '../store';
import { detailOf, summaryOf, type PortalStore } from '../seed/portal';
import type { Brand, Member, OrganizationDetail, OrgRole } from '@/lib/types';
import type { CatalogueRow, OrgPortalSettings } from '@/pages/OrganizationDetailPage';
import type { Agreement } from '@/lib/agreements';
import type { ConciergeQuestionRow } from '@/pages/org-questions-tab';

/**
 * Client organizations and their tabs. Northwind Group carries every tab's
 * content; any other organization (the two in the list, or one made in the
 * sandbox) opens with its tabs empty, the way a new client starts.
 */

function organizationOr404(portal: PortalStore, id: string): OrganizationDetail {
  const found = portal.organizations.find((organization) => organization.id === id);
  if (!found) throw new SandboxError(404, 'Organization not found.');
  return found;
}

function isNorthwind(store: SandboxStore, id: string): boolean {
  return id === store.portal.organizationId;
}

function replaceOrganization(portal: PortalStore, updated: OrganizationDetail): void {
  portal.organizations = portal.organizations.map((organization) => (organization.id === updated.id ? updated : organization));
}

function slugOf(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

const EMPTY_SETTINGS = (portal: PortalStore): OrgPortalSettings => ({ conciergeEmail: null, conciergePhone: null, defaults: portal.settings.defaults });

export const organizationRoutes: readonly Route[] = [
  // Fixed paths first: `:id` would match them.
  route('GET', '/organizations/slug-check', ({ store, query }) => {
    const slug = slugOf(query.get('slug') ?? '');
    const taken = store.portal.organizations.some((organization) => organization.slug === slug);
    return { data: { slug, available: !taken && slug.length >= 2, ...(taken ? { reason: 'taken', message: 'Another portal uses that address.' } : {}), portalUrl: `https://${slug}.portal.example` } };
  }),
  route('GET', '/organizations/salesforce-accounts', ({ query }) => {
    const q = (query.get('q') ?? '').toLowerCase();
    const hits = [
      { id: '001NW00000DEMO1AAA', name: 'Northwind Group', type: 'Customer', billing: 'Minneapolis, MN', parent: null },
      { id: '001NW00000DEMO2AAA', name: 'Northwind Group (Denver)', type: 'Customer', billing: 'Denver, CO', parent: { id: '001NW00000DEMO1AAA', name: 'Northwind Group' } },
    ];
    return { data: hits.filter((hit) => hit.name.toLowerCase().includes(q)) };
  }),
  route('GET', '/organizations', ({ store, query }) => {
    const kind = query.get('kind');
    const rows = store.portal.organizations
      .map(summaryOf)
      .filter((row) => (kind ? (row.kind ?? 'CLIENT') === kind : true))
      .filter((row) => matches(query.get('search'), row.name, row.slug))
      .sort((a, b) => a.name.localeCompare(b.name));
    return paginate(rows, query, 25);
  }),
  route('POST', '/organizations', ({ store, body }) => {
    const input = body as { name: string; slug: string; brand?: { primaryColor?: string; sidebarColor?: string } };
    const id = newId();
    const detail = detailOf({ id, name: input.name, slug: input.slug, customDomain: null, status: 'ACTIVE', createdAt: now(), memberCount: 0, portalUrl: `https://${input.slug}.portal.example`, brand: null, kind: 'CLIENT', isDemo: false });
    const brand: Brand = {
      id: newId(),
      organizationId: id,
      logoUrl: null,
      primaryColor: input.brand?.primaryColor ?? '#1851d1',
      sidebarColor: input.brand?.sidebarColor ?? '#0e2e77',
      accentColor: '#ffc72c',
      fontFamily: 'Arial, Helvetica, sans-serif',
      heroImageUrl: null,
      heroQuote: null,
      heroStatValue: null,
      heroStatLabel: null,
      loginEyebrow: null,
      loginHeadline: null,
      loginSubcopy: null,
      welcomeMessage: null,
    };
    const created = { ...detail, brand };
    store.portal.organizations = [...store.portal.organizations, created];
    return { data: created };
  }),
  route('GET', '/organizations/:id', ({ store, params }) => ({ data: organizationOr404(store.portal, params['id'] ?? '') })),
  route('PATCH', '/organizations/:id', ({ store, params, body }) => {
    const updated = { ...organizationOr404(store.portal, params['id'] ?? ''), ...(body as Partial<OrganizationDetail>) };
    replaceOrganization(store.portal, updated);
    return { data: updated };
  }),
  route('PUT', '/organizations/:id/brand', ({ store, params, body }) => {
    const organization = organizationOr404(store.portal, params['id'] ?? '');
    if (!organization.brand) throw new SandboxError(404, 'This organization has no branding yet.');
    const brand: Brand = { ...organization.brand, ...(body as Partial<Brand>) };
    replaceOrganization(store.portal, { ...organization, brand });
    return { data: brand };
  }),

  route('GET', '/organizations/:id/settings', ({ store, params }) => ({ data: isNorthwind(store, params['id'] ?? '') ? store.portal.settings : EMPTY_SETTINGS(store.portal) })),
  route('PATCH', '/organizations/:id/settings', ({ store, params, body }) => {
    if (!isNorthwind(store, params['id'] ?? '')) return { data: { ...EMPTY_SETTINGS(store.portal), ...(body as object) } };
    store.portal.settings = { ...store.portal.settings, ...(body as Partial<OrgPortalSettings>) };
    return { data: store.portal.settings };
  }),

  route('GET', '/organizations/:id/credits', ({ store, params }) => ({
    data: isNorthwind(store, params['id'] ?? '') ? store.portal.credits : { balance: { available: 0, purchased: 0, reserved: 0, committed: 0, consumed: 0 } },
  })),
  route('GET', '/organizations/:id/credits/ledger', ({ store, params, query }) => {
    const rows = isNorthwind(store, params['id'] ?? '') ? [...store.portal.ledger].sort((a, b) => b.createdAt.localeCompare(a.createdAt)) : [];
    return paginate(rows, query, 10);
  }),
  route('POST', '/organizations/:id/credits', ({ store, params, body }) => {
    if (!isNorthwind(store, params['id'] ?? '')) throw new SandboxError(409, 'This client has no agreement in the sandbox. Try it on Northwind Group.');
    const input = body as { amount: number; reference?: string; reason?: string };
    if (!Number.isFinite(input.amount) || input.amount <= 0) throw new SandboxError(400, 'Enter a number of credits above zero.');
    store.portal.ledger = [{ id: newId(), type: 'PURCHASE', amount: input.amount, ...(input.reference ? { reference: input.reference } : {}), ...(input.reason ? { reason: input.reason } : {}), actorName: 'You', createdAt: now() }, ...store.portal.ledger];
    const balance = store.portal.credits.balance;
    store.portal.credits = { balance: { ...balance, available: balance.available + input.amount, purchased: balance.purchased + input.amount } };
    return { data: store.portal.credits };
  }),
  route('GET', '/organizations/:id/credit-change-requests', () => ({ data: [] })),

  route('GET', '/organizations/:id/members', ({ store, params }) => ({ data: isNorthwind(store, params['id'] ?? '') ? store.portal.members : [] })),
  route('POST', '/organizations/:id/members', ({ store, params, body }) => {
    if (!isNorthwind(store, params['id'] ?? '')) throw new SandboxError(409, 'Try adding members on Northwind Group in the sandbox.');
    const input = body as { email: string; firstName: string; lastName: string; role: OrgRole; sendInvitation: boolean };
    const member: Member = { id: newId(), email: input.email, firstName: input.firstName, lastName: input.lastName, title: null, role: input.role, teams: [], creditApprovalLimit: null, eventSizeLimit: null, isActive: true, isPending: input.sendInvitation, department: null, region: null };
    store.portal.members = [...store.portal.members, member];
    return { data: member };
  }),
  route('PATCH', '/organizations/:id/members/:memberId', ({ store, params, body }) => {
    const existing = store.portal.members.find((member) => member.id === params['memberId']);
    if (!existing) throw new SandboxError(404, 'Member not found.');
    const updated = { ...existing, ...(body as Partial<Member>) };
    store.portal.members = store.portal.members.map((member) => (member.id === updated.id ? updated : member));
    return { data: updated };
  }),

  route('GET', '/organizations/:id/catalogue', ({ store, params }) => ({ data: isNorthwind(store, params['id'] ?? '') ? store.portal.catalogue : [] })),
  route('GET', '/organizations/:id/catalogue/programs', ({ store, params, query }) => {
    const chosen = new Set(isNorthwind(store, params['id'] ?? '') ? store.portal.catalogue.map((row) => row.v3ProgramId) : []);
    const library = store.portal.programs.filter((program) => !chosen.has(program.id)).map((program) => ({ v3ProgramId: program.id, name: program.name, category: null }));
    return { data: library.filter((program) => matches(query.get('search'), program.name)) };
  }),
  route('POST', '/organizations/:id/catalogue', ({ store, params, body }) => {
    if (!isNorthwind(store, params['id'] ?? '')) throw new SandboxError(409, 'Try the catalog on Northwind Group in the sandbox.');
    const programId = (body as { v3ProgramId: string }).v3ProgramId;
    const program = store.portal.programs.find((candidate) => candidate.id === programId);
    if (!program) throw new SandboxError(404, 'Program not found.');
    const row: CatalogueRow = { id: newId(), v3ProgramId: program.id, name: program.name, imageUrl: null, isEnabled: true, isFeatured: false, startingCredits: 60, leadTimeDays: 21, requiresReview: false, pricingTemplateId: null, pricingTemplateName: null, tiers: [], baseCredits: 60, includedParticipants: 20, incrementSize: 10, incrementCredits: 8, ownLeadTimeDays: 21 };
    store.portal.catalogue = [...store.portal.catalogue, row];
    return { data: row };
  }),
  route('PATCH', '/organizations/:id/catalogue/:entryId', ({ store, params, body }) => {
    const existing = store.portal.catalogue.find((row) => row.id === params['entryId']);
    if (!existing) throw new SandboxError(404, 'Catalog entry not found.');
    const updated = { ...existing, ...(body as Partial<CatalogueRow>) };
    store.portal.catalogue = store.portal.catalogue.map((row) => (row.id === updated.id ? updated : row));
    return { data: updated };
  }),
  route('DELETE', '/organizations/:id/catalogue/:entryId', ({ store, params }) => {
    store.portal.catalogue = store.portal.catalogue.filter((row) => row.id !== params['entryId']);
    return { data: null };
  }),
  route('GET', '/pricing-templates', () => ({ data: [] })),

  route('GET', '/organizations/:id/agreement', ({ store, params }) => ({ data: isNorthwind(store, params['id'] ?? '') ? store.portal.agreement : { agreement: null, history: [], requests: [] } })),
  route('PUT', '/organizations/:id/agreement', ({ store, params, body }) => {
    if (!isNorthwind(store, params['id'] ?? '')) throw new SandboxError(409, 'Try the agreement on Northwind Group in the sandbox.');
    const current = store.portal.agreement.agreement;
    if (!current) throw new SandboxError(404, 'No agreement.');
    const agreement: Agreement = { ...current, ...(body as Partial<Agreement>) };
    store.portal.agreement = { ...store.portal.agreement, agreement };
    return { data: agreement };
  }),
  route('GET', '/organizations/:id/salesforce-accounts', ({ store, params }) => ({ data: isNorthwind(store, params['id'] ?? '') ? store.portal.salesforce : { accounts: [], salesforceReachable: true } })),
  route('GET', '/organizations/:id/messaging-profile', ({ store, params }) => ({ data: isNorthwind(store, params['id'] ?? '') ? store.portal.messaging : null })),
  route('PUT', '/organizations/:id/messaging-profile', ({ store, params, body }) => {
    if (isNorthwind(store, params['id'] ?? '')) store.portal.messaging = { ...store.portal.messaging, ...(body as Record<string, unknown>) };
    return { data: store.portal.messaging };
  }),

  route('GET', '/organizations/:id/large-events', ({ store, params }) => ({ data: isNorthwind(store, params['id'] ?? '') ? store.portal.inquiries : [] })),
  route('PATCH', '/organizations/:id/large-events/:inquiryId', ({ store, params, body }) => {
    store.portal.inquiries = store.portal.inquiries.map((row) => (row.id === params['inquiryId'] ? { ...row, ...(body as object) } : row));
    return { data: store.portal.inquiries.find((row) => row.id === params['inquiryId']) ?? null };
  }),
  route('GET', '/organizations/:id/bookings/review-queue', () => ({ data: { factors: [], outcomes: [], bookings: [] } })),
  route('GET', '/organizations/:id/workspaces', () => ({ data: [] })),
  route('GET', '/campaigns', () => ({ data: [] })),

  route('GET', '/organizations/:id/concierge', ({ store, params }) => ({ data: isNorthwind(store, params['id'] ?? '') ? store.portal.concierge : [] })),
  route('POST', '/organizations/:id/concierge/:questionId/answer', ({ store, params, body }) => {
    const response = String((body as { response?: string }).response ?? '').trim();
    if (response === '') throw new SandboxError(400, 'Write an answer first.');
    const existing = store.portal.concierge.find((row) => row.id === params['questionId']);
    if (!existing) throw new SandboxError(404, 'Question not found.');
    const answered: ConciergeQuestionRow = { ...existing, status: 'ANSWERED', response, respondedAt: now(), respondedBy: 'You' };
    store.portal.concierge = store.portal.concierge.map((row) => (row.id === answered.id ? answered : row));
    return { data: answered };
  }),
];
